import { Badge, Card, DataList, Flex, Text } from '@radix-ui/themes';
import { IPlatePoint } from 'interfaces/i-pitch-performance';
import { MAX_PLATE_DELTA_IN } from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TooltipProps } from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

const formatDelta = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '-' : ''}${Math.abs(value).toFixed(1)}"`;

export const PlateTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point: IPlatePoint | undefined = payload[0].payload;

  if (!point) {
    return null;
  }

  return (
    <Card size="1">
      <DataList.Root size="1" orientation="vertical" mb="3">
        <DataList.Item>
          <DataList.Label>Pitch Name</DataList.Label>
          <DataList.Value>
            <svg width="14" height="14" viewBox="0 0 14 14">
              <circle
                cx="7"
                cy="7"
                r="3.75"
                fill={`rgb(${point.fillRgb})`}
                fillOpacity="0.75"
                stroke={`rgb(${point.strokeRgb})`}
                strokeWidth="0.5"
              />
            </svg>
            &nbsp;
            {point.pitchName}
          </DataList.Value>
        </DataList.Item>

        {point.msHash && (
          <DataList.Item>
            <DataList.Label>Machine State</DataList.Label>
            <DataList.Value># {point.msHash.slice(-4)}</DataList.Value>
          </DataList.Item>
        )}
      </DataList.Root>

      <DataList.Root size="1" orientation="horizontal">
        <DataList.Item>
          <DataList.Label>
            {point.isTarget ? 'Target X (in)' : 'Plate X (in)'}
          </DataList.Label>
          <DataList.Value>
            <Flex gap="1" width="100%" align="baseline" justify="between">
              {!point.onlyShowDeltas && (
                <Text size="1">{point.x.toFixed(1)}</Text>
              )}

              {point.delta && (
                <Badge
                  size="1"
                  color={
                    Math.abs(point.delta.x) > MAX_PLATE_DELTA_IN
                      ? RADIX.COLOR.DANGER
                      : RADIX.COLOR.SUCCESS
                  }
                >
                  {formatDelta(point.delta.x)}
                </Badge>
              )}
            </Flex>
          </DataList.Value>
        </DataList.Item>

        <DataList.Item>
          <DataList.Label>
            {point.isTarget ? 'Target Z (in)' : 'Plate Z (in)'}
          </DataList.Label>
          <DataList.Value>
            <Flex gap="1" width="100%" align="baseline" justify="between">
              {!point.onlyShowDeltas && (
                <Text size="1">{point.y.toFixed(1)}</Text>
              )}

              {point.delta && (
                <Badge
                  size="1"
                  color={
                    Math.abs(point.delta.y) > MAX_PLATE_DELTA_IN
                      ? RADIX.COLOR.DANGER
                      : RADIX.COLOR.SUCCESS
                  }
                >
                  {formatDelta(point.delta.y)}
                </Badge>
              )}
            </Flex>
          </DataList.Value>
        </DataList.Item>
      </DataList.Root>
    </Card>
  );
};
